import { meResponseSchema } from '@coworker/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuth } from '../auth/AuthProvider.js';
import { ApiError } from '../lib/api-error.js';
import { Button } from '../components/ui/button.js';
import { SESSION_QUERY_KEY } from '../hooks/use-session.js';

/**
 * The screen a valid token with no profile lands on - AUTH-010.
 *
 * `403 USER_NOT_PROVISIONED` was a dead end for every new account: the API
 * deliberately refuses to invent a user from an access token, because one
 * carries no email and no name. This is the flow that supplies them - the email
 * from the id token the server verifies, the display name and workspace name
 * from the person.
 */
export function Onboarding() {
  const { api, idToken, expired } = useAuth();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');

  const provision = useMutation({
    mutationFn: () => {
      const token = idToken();
      if (token === null) throw new Error('this session has no id token; sign in again');

      return api.request('/v1/onboarding', {
        method: 'POST',
        schema: meResponseSchema,
        body: {
          idToken: token,
          ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
          ...(workspaceName.trim() === '' ? {} : { workspaceName: workspaceName.trim() }),
        },
      });
    },
    // The response is the same shape `/v1/me` returns, so the shell can render
    // from it without a second round trip.
    onSuccess: (me) => queryClient.setQueryData(SESSION_QUERY_KEY, me),
    // The id token dies with the access token at fifteen minutes, and this is
    // the one screen where a person stops to type. An expired one comes back as
    // the gateway's bare 401 and used to render below as inert text - on a
    // screen that deliberately has no header, so there was nothing on it to
    // sign in with and the only button could never succeed again. This is the
    // same handler `AppShell` gives an expired session query, for the same
    // reason: an ended session is the ordinary end of one, not an error.
    onError: (error) => {
      if (error instanceof ApiError && error.isUnauthenticated) expired();
    },
  });

  return (
    <section className="mx-auto flex max-w-md flex-col gap-4 p-8">
      <h1 className="text-xl font-semibold">Set up your workspace</h1>
      <p className="text-sm text-muted-foreground">
        Your account is verified. This creates your first workspace and the Generalist who works in
        it.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        What should we call you?
        <input
          className="rounded-[--radius] border border-border px-3 py-2"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Taken from your account if you leave this blank"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Workspace name
        <input
          className="rounded-[--radius] border border-border px-3 py-2"
          value={workspaceName}
          onChange={(event) => setWorkspaceName(event.target.value)}
          placeholder="Named after you if you leave this blank"
        />
      </label>

      {provision.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {provision.error instanceof Error ? provision.error.message : 'Something went wrong'}
        </p>
      ) : null}

      <Button onClick={() => provision.mutate()} disabled={provision.isPending}>
        {provision.isPending ? 'Creating…' : 'Create my workspace'}
      </Button>
    </section>
  );
}
